import { useEffect, useState, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Phone, MessageCircle, FileText, CheckCircle, Eye, Activity, RefreshCw, Loader2, AlertCircle,
} from 'lucide-react';

const fmtDate = (s) => s ? new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const AdminTracking = () => {
  const [days, setDays] = useState(30);
  const [utmSource, setUtmSource] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ days: String(days) });
      if (utmSource) qs.append('utm_source', utmSource);
      if (utmCampaign) qs.append('utm_campaign', utmCampaign);

      const statsRes = await api.get(`/admin/tracking/stats?${qs.toString()}`);
      setStats(statsRes.data);

      const evQs = new URLSearchParams({ limit: '50' });
      if (utmSource) evQs.append('utm_source', utmSource);
      if (utmCampaign) evQs.append('utm_campaign', utmCampaign);
      if (eventFilter) evQs.append('event_name', eventFilter);
      const evRes = await api.get(`/admin/tracking/events?${evQs.toString()}`);
      setEvents(evRes.data.items || []);

      const srcRes = await api.get('/admin/tracking/sources').catch(() => ({ data: [] }));
      setSources(srcRes.data || []);
    } catch (e) {
      console.error('[AdminTracking] load failed', e);
      setError({
        status: e.response?.status,
        detail: e.response?.data?.detail || e.message,
        url: e.config?.url,
      });
    } finally {
      setLoading(false);
    }
  }, [days, utmSource, utmCampaign, eventFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Conversion-Tracking">
      {/* Filter */}
      <div className="bg-white border rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div>
          <Label className="text-xs">Zeitraum (Tage)</Label>
          <Input
            type="number" min={1} max={365}
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value || '30', 10))}
            data-testid="tracking-filter-days"
          />
        </div>
        <div>
          <Label className="text-xs">utm_source</Label>
          <Input value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="z.B. google" data-testid="tracking-filter-source" />
        </div>
        <div>
          <Label className="text-xs">utm_campaign</Label>
          <Input value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="z.B. sanierung" data-testid="tracking-filter-campaign" />
        </div>
        <div>
          <Label className="text-xs">Event</Label>
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="w-full h-10 border border-gray-200 rounded-md bg-white px-2 text-sm"
            data-testid="tracking-filter-event"
          >
            <option value="">alle</option>
            <option value="whatsapp_click">whatsapp_click</option>
            <option value="call_click">call_click</option>
            <option value="form_open">form_open</option>
            <option value="form_submit">form_submit</option>
            <option value="lead_thank_you_view">lead_thank_you_view</option>
          </select>
        </div>
        <Button onClick={load} disabled={loading} className="bg-[#4A331D] hover:bg-[#593B23]" data-testid="tracking-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex gap-2 items-start" data-testid="tracking-error">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
          <div className="text-sm text-red-800">
            <div className="font-semibold">Tracking konnte nicht geladen werden</div>
            <div className="text-xs">Status {error.status || '–'} · {error.detail} · {error.url}</div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="tracking-stats">
          <Stat icon={MessageCircle} color="bg-green-50 text-green-700" label="WhatsApp-Klicks" value={stats.whatsapp_clicks} />
          <Stat icon={Phone} color="bg-blue-50 text-blue-700" label="Anrufe" value={stats.call_clicks} />
          <Stat icon={FileText} color="bg-amber-50 text-amber-700" label="Formular geöffnet" value={stats.form_opens} />
          <Stat icon={CheckCircle} color="bg-emerald-50 text-emerald-700" label="Formular gesendet" value={stats.form_submits} />
          <Stat icon={Eye} color="bg-purple-50 text-purple-700" label="Danke-Seite" value={stats.lead_thank_you_views} />
          <Stat icon={Activity} color="bg-[#4A331D]/10 text-[#4A331D]" label="Conversion-Rate" value={`${stats.conversion_rate_pct}%`} />
        </div>
      )}

      {/* UTM Sources */}
      {sources.length > 0 && (
        <div className="bg-white border rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-[#4A331D] mb-3">Top Quellen (UTM)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sources.slice(0, 8).map((s) => (
              <div key={`${s.utm_source}-${s.utm_campaign}`} className="text-xs flex justify-between bg-gray-50 rounded px-2 py-1">
                <span><b>{s.utm_source}</b>{s.utm_campaign ? ` / ${s.utm_campaign}` : ''}</span>
                <span className="font-mono">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event list */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#4A331D]">Letzte Events ({events.length})</h3>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>
        {events.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">Keine Events im gewählten Zeitraum.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Zeit</th>
                  <th className="px-3 py-2 text-left">Event</th>
                  <th className="px-3 py-2 text-left">Quelle</th>
                  <th className="px-3 py-2 text-left">Kampagne</th>
                  <th className="px-3 py-2 text-left">Seite</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                    <td className="px-3 py-1.5 font-medium text-[#4A331D]">{e.event_name}</td>
                    <td className="px-3 py-1.5">{e.utm_source || '-'}</td>
                    <td className="px-3 py-1.5">{e.utm_campaign || '-'}</td>
                    <td className="px-3 py-1.5 text-gray-500 truncate max-w-[200px]">{e.page_url || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const Stat = ({ icon: Icon, color, label, value }) => (
  <div className="bg-white rounded-xl border p-3" data-testid={`tracking-stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <div className={`inline-flex w-8 h-8 rounded-lg items-center justify-center mb-1 ${color}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-xl font-bold text-[#4A331D]">{value}</div>
  </div>
);

export default AdminTracking;
